import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import CrosswordGrid from '../components/CrosswordGrid';
import CluesList from '../components/CluesList';
import ShareModal from '../components/ShareModal';
import CompletionModal from '../components/CompletionModal';
import { getSession, updateSessionActivity } from '../services/sessionService';
import { generateCrosswordGrid } from '../utils/crosswordGenerator';

const { FiShare2, FiEdit3, FiArrowLeft, FiUsers } = FiIcons;

function SessionView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [grid, setGrid] = useState(null);
  const [userAnswers, setUserAnswers] = useState({});
  const [activeWord, setActiveWord] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadSession();
  }, [id]);

  useEffect(() => {
    if (session && grid) {
      checkCompletion();
    }
  }, [userAnswers, session, grid]);

  const loadSession = async () => {
    try {
      setLoading(true);
      const sessionData = await getSession(id);
      
      if (!sessionData) {
        setError('Session nicht gefunden');
        return;
      }

      setSession(sessionData);
      const generatedGrid = generateCrosswordGrid(sessionData.clues);
      setGrid(generatedGrid);
      
      // Update last activity
      await updateSessionActivity(id);
    } catch (err) {
      console.error('Error loading session:', err);
      setError('Fehler beim Laden der Session');
    } finally {
      setLoading(false);
    }
  };

  const checkCompletion = () => {
    if (!grid || !session) return;

    const allWordsCorrect = grid.words.every(word => {
      const wordAnswer = word.cells.map(cell => 
        userAnswers[`${cell.row}-${cell.col}`] || ''
      ).join('').toUpperCase();
      
      return wordAnswer === word.answer.toUpperCase();
    });

    if (allWordsCorrect && grid.words.length > 0) {
      setShowCompletionModal(true);
    }
  };

  const handleCellChange = (row, col, value) => {
    const key = `${row}-${col}`;
    setUserAnswers(prev => ({
      ...prev,
      [key]: value.toUpperCase()
    }));
  };

  const handleWordSelect = (word) => {
    setActiveWord(word);
  };

  const getCompletedWords = () => {
    if (!grid) return [];
    
    return grid.words.filter(word => {
      const wordAnswer = word.cells.map(cell => 
        userAnswers[`${cell.row}-${cell.col}`] || ''
      ).join('').toUpperCase();
      
      return wordAnswer === word.answer.toUpperCase();
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-primary font-rubik">Lade Kreuzworträtsel...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4 font-rubik">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-button text-white rounded-md hover:bg-button-hover font-rubik"
          >
            Zur Startseite
          </button>
        </div>
      </div>
    );
  }

  const completedWords = getCompletedWords();
  const progressPercentage = grid ? Math.round((completedWords.length / grid.words.length) * 100) : 0;

  return (
    <div className="min-h-screen bg-white">
      <div className="container mx-auto px-4 py-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
            <div>
              <button
                onClick={() => navigate('/')}
                className="flex items-center gap-2 text-primary hover:opacity-80 mb-2 font-rubik"
              >
                <SafeIcon icon={FiArrowLeft} />
                Zurück zur Startseite
              </button>
              <h1 className="text-2xl sm:text-3xl font-bold text-primary font-rubik">
                Kreuzworträtsel
              </h1>
            </div>
            
            <div className="flex gap-2">
              <button
                onClick={() => setShowShareModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-button text-white rounded-md hover:bg-button-hover transition-colors font-rubik"
              >
                <SafeIcon icon={FiShare2} />
                Teilen
              </button>
              <button
                onClick={() => navigate(`/edit/${id}`)}
                className="flex items-center gap-2 px-4 py-2 bg-button text-white rounded-md hover:bg-button-hover transition-colors font-rubik"
              >
                <SafeIcon icon={FiEdit3} />
                Bearbeiten
              </button>
            </div>
          </div>

          <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <SafeIcon icon={FiUsers} className="text-primary" />
                <span className="text-sm text-primary font-rubik">
                  Session ID: {id}
                </span>
              </div>
              <div className="text-right">
                <div className="text-sm text-primary mb-1 font-rubik">
                  Fortschritt: {completedWords.length} / {grid?.words.length || 0} Wörter
                </div>
                <div className="w-32 bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-button h-2 rounded-full transition-all duration-300"
                    style={{ width: `${progressPercentage}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {grid && (
          <div className="grid lg:grid-cols-3 gap-6">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="lg:col-span-2"
            >
              <CrosswordGrid
                grid={grid}
                userAnswers={userAnswers}
                activeWord={activeWord}
                onCellChange={handleCellChange}
                onWordSelect={handleWordSelect}
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <CluesList
                words={grid.words}
                activeWord={activeWord}
                completedWords={completedWords}
                onWordSelect={handleWordSelect}
              />
            </motion.div>
          </div>
        )}

        {showShareModal && (
          <ShareModal
            sessionId={id}
            onClose={() => setShowShareModal(false)}
          />
        )}

        {showCompletionModal && session && (
          <CompletionModal
            message={session.completionMessage}
            onClose={() => setShowCompletionModal(false)}
          />
        )}
      </div>
    </div>
  );
}

export default SessionView;